import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiCheck, FiHeart, FiShoppingBag } from 'react-icons/fi';
import { useCart } from '../contexts/CartContext';

const Card = styled.article`
  position: relative;
  min-width: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #e0dbd1;
  border-radius: 18px;
  background: #fff;
  transition: transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease;

  &:hover {
    transform: translateY(-5px);
    border-color: #b9aa99;
    box-shadow: 0 24px 50px rgba(34, 31, 26, .11);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ImageLink = styled(Link)`
  position: relative;
  aspect-ratio: 1.08 / 1;
  overflow: hidden;
  display: block;
  background: linear-gradient(145deg, #f5f2ec 0%, #ece7dd 100%);

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
    padding: 18px;
    transition: transform 350ms cubic-bezier(.22, 1, .36, 1);
  }

  ${Card}:hover & img {
    transform: scale(1.035);
  }

  &:focus-visible {
    outline: 3px solid rgba(182, 90, 58, .42);
    outline-offset: -4px;
  }

  @media (prefers-reduced-motion: reduce) {
    img { transition: none; }
  }
`;

const BrandLabel = styled.span`
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 1;
  padding: 7px 11px;
  border: 1px solid rgba(38, 40, 36, .11);
  border-radius: 999px;
  background: rgba(255, 255, 255, .86);
  color: #55584f;
  font-size: 10px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: .1em;
  text-transform: uppercase;
  backdrop-filter: blur(8px);
`;

const FavoriteButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 44px;
  height: 44px;
  border: 1px solid ${props => props.$active ? '#a34d32' : 'rgba(38, 40, 36, .12)'};
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${props => props.$active ? '#a34d32' : 'rgba(255, 255, 255, .9)'};
  color: ${props => props.$active ? '#fff' : '#30322d'};
  backdrop-filter: blur(8px);
  transition: background 180ms ease, color 180ms ease, transform 180ms ease;

  &:hover {
    transform: scale(1.05);
    background: #a34d32;
    color: #fff;
  }

  &:focus-visible {
    outline: 3px solid rgba(182, 90, 58, .42);
    outline-offset: 3px;
  }
`;

const Body = styled.div`
  flex: 1;
  padding: 22px;
  display: flex;
  flex-direction: column;

  @media (max-width: 560px) {
    padding: 18px;
  }
`;

const TitleLink = styled(Link)`
  color: #20211e;

  h3 {
    min-height: 52px;
    margin: 0;
    display: -webkit-box;
    overflow: hidden;
    font-size: 18px;
    line-height: 1.42;
    font-weight: 700;
    letter-spacing: -.015em;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &:hover { color: #8c452f; }

  &:focus-visible {
    outline: 3px solid rgba(182, 90, 58, .42);
    outline-offset: 4px;
  }
`;

const Meta = styled.div`
  min-height: 26px;
  margin-top: 10px;
  color: #73756e;
  font-size: 13px;
  line-height: 1.4;
`;

const Footer = styled.div`
  margin-top: auto;
  padding-top: 22px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const Price = styled.div`
  strong {
    display: block;
    color: #20211e;
    font-size: 25px;
    line-height: 1;
    font-weight: 850;
    letter-spacing: -.03em;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  del {
    display: block;
    margin-top: 6px;
    color: #96978f;
    font-size: 13px;
  }
`;

const CartButton = styled.button`
  min-width: 148px;
  min-height: 48px;
  padding: 0 17px;
  border: 1px solid ${props => props.$inCart ? '#657456' : '#b65a3a'};
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  background: ${props => props.$inCart ? '#657456' : '#b65a3a'};
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  transition: background 180ms ease, transform 180ms ease, box-shadow 180ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: ${props => props.$inCart ? '#566448' : '#98472f'};
    box-shadow: 0 10px 22px rgba(73, 49, 37, .16);
  }

  &:disabled {
    border-color: #d5d2cb;
    background: #d5d2cb;
    color: #777972;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid rgba(182, 90, 58, .42);
    outline-offset: 3px;
  }

  @media (max-width: 560px) {
    width: 100%;
  }
`;

const formatPrice = value => `${(Number(value) || 0).toLocaleString('ru-RU')} ₽`;

const HomeProductCard = ({ product, isFavorite, onToggleFavorite }) => {
  const { addToCart, isInCart, getItemQuantity } = useCart();
  const inCart = isInCart(product.id);
  const quantity = getItemQuantity(product.id);
  const inStock = product.inStock ?? (Number(product.total_stock_quantity) > 0);
  const productUrl = `/product/${product.id}`;

  const handleAddToCart = event => {
    event.preventDefault();
    if (!inStock) return;
    addToCart(product);
  };

  const handleFavorite = event => {
    event.preventDefault();
    onToggleFavorite(product.id, isFavorite);
  };

  return (
    <Card>
      <ImageLink to={productUrl} aria-label={`Открыть товар «${product.name}»`}>
        <BrandLabel>{product.brand || 'Lagom Life'}</BrandLabel>
        <img src={product.image || '/nofoto.png'} alt={product.name} loading="lazy" />
      </ImageLink>

      <FavoriteButton
        type="button"
        $active={isFavorite}
        aria-label={isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
        aria-pressed={isFavorite}
        onClick={handleFavorite}
      >
        <FiHeart aria-hidden="true" />
      </FavoriteButton>

      <Body>
        <TitleLink to={productUrl}><h3>{product.name}</h3></TitleLink>
        <Meta>{inStock ? 'В наличии · доставка по России' : 'Уточните наличие'}</Meta>

        <Footer>
          <Price>
            <strong>{formatPrice(product.price)}</strong>
            {product.oldPrice ? <del>{formatPrice(product.oldPrice)}</del> : null}
          </Price>
          <CartButton type="button" $inCart={inCart} disabled={!inStock} onClick={handleAddToCart}>
            {inCart ? <FiCheck aria-hidden="true" /> : <FiShoppingBag aria-hidden="true" />}
            {!inStock ? 'Нет в наличии' : inCart ? `В корзине · ${quantity}` : 'В корзину'}
          </CartButton>
        </Footer>
      </Body>
    </Card>
  );
};

export default HomeProductCard;
